import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';

import { DynamicIcon } from '@frontend-libs/atomic';

import { type PortalTheme } from '../../theme/';
import { Stamp } from './stamp.component';

export interface StampCardListItemProps {
  title: string;
  currentStamps: number;
  maxStamps: number;
  onClick?: () => void;
  ariaLabel?: string;
  fullWidth?: boolean;
  showBorder?: boolean;
}

export const StampCard = (props: StampCardListItemProps) => {
  const theme = useTheme<PortalTheme>();

  const claimPercent = useMemo(() => {
    if (props.maxStamps <= 0) return 0;
    return Math.min((props.currentStamps / props.maxStamps) * 100, 100);
  }, [props.currentStamps, props.maxStamps]);

  const chips = useMemo(() => {
    const chips = [];

    if (props.maxStamps <= 30) {
      for (let i = 1; i <= props.maxStamps; i++) {
        chips.push(
          <Stamp key={i} claimed={i <= props.currentStamps} stampNumber={i} />,
        );
      }
    }

    return chips;
  }, [props.currentStamps, props.maxStamps]);

  return (
    <Card
      sx={{
        width: props.fullWidth ? '100%' : '320px',
        borderRadius: `2*${theme.shape.borderRadius}px`,
        border: props.showBorder ? `1px solid ${theme.darkDivider}` : '0',
        padding: 0,
        margin: 0,
        backgroundColor: `${theme.palette.background.paper}`,
        boxShadow: theme.boxShadow,
        position: 'relative',
      }}
      onClick={() => props.onClick?.()}
      aria-label={props.ariaLabel}
    >
      <CardContent
        sx={{
          width: props.fullWidth ? '100%' : props.onClick ? undefined : '320px',
          ':last-child': { paddingBottom: theme.spacing(2) },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            paddingBottom: theme.spacing(2),
          }}
        >
          <Typography variant="h4">{props.title}</Typography>
          <Stack>
            {props.onClick && (
              <DynamicIcon
                width={24}
                height={24}
                iconId="info"
                strokeColor={theme.palette.text.primary}
              />
            )}
          </Stack>
        </Stack>
        {props.maxStamps > 30 && (
          <Stack sx={{ position: 'relative', height: '40px' }}>
            <Card
              sx={{
                width: '100%',
                height: '40px',
                borderRadius: `${theme.shape.borderRadius}px`,
                border: `1px solid ${theme.palette.divider}`,
                padding: 0,
                margin: 0,
                backgroundColor: 'transparent',
                position: 'absolute',
              }}
            >
              <Stack
                direction="row"
                spacing={0}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="subtitle1">
                  {props.currentStamps}/{props.maxStamps}
                </Typography>
              </Stack>
            </Card>
            <Card
              sx={{
                width: '100%',
                height: '40px',
                borderRadius: `${theme.shape.borderRadius}px`,
                border: `1px solid ${theme.palette.divider}`,
                padding: 0,
                margin: 0,
                backgroundColor: theme.palette.primary.main,
                position: 'absolute',
                top: 0,
                left: 0,
                clipPath: `polygon(0 0, ${claimPercent}% 0%, ${claimPercent}% 100%, 0% 100%)`,
              }}
            >
              <Stack
                direction="row"
                spacing={0}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: theme.palette.primary.contrastText }}
                >
                  {props.currentStamps}/{props.maxStamps}
                </Typography>
              </Stack>
            </Card>
          </Stack>
        )}
        {props.maxStamps <= 30 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '5px',
            }}
          >
            {chips}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
